import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import GalleryLightbox from '../components/GalleryLightbox'

export default function Tag() {
  const [galleryImages, setGalleryImages] = useState([])
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [projects, setProjects] = useState([])
  const contentRef = useRef(null)
  const [contentHeight, setContentHeight] = useState(null)

  useEffect(() => {
    if (!contentRef.current) return
    const updateHeight = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.offsetHeight)
      }
    }
    updateHeight()
    const ro = new ResizeObserver(updateHeight)
    ro.observe(contentRef.current)
    window.addEventListener('resize', updateHeight)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', updateHeight)
    }
  }, [])

  useEffect(() => {
    // Fetch gallery for tag
    fetch('https://api.dev.auxo.dk/public/v2/content/galleri')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          const match = data.items.find((item) => item.url_slug === 'tag' || item.tilknyttet_ydelse === 'tag')
          if (match && Array.isArray(match.billeder)) {
            setGalleryImages(match.billeder)
          }
        }
      })
      .catch(() => {})

    // Fetch related projects
    fetch('https://api.dev.auxo.dk/public/v2/content/projekter')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          const filtered = data.items.filter((p) => p.projektkategori === 'Tag' || p.relaterede_ydelser === 'tag')
          setProjects(filtered)
        }
      })
      .catch(() => {})
  }, [])

  return (
    <>
      <SEOHead
        title="Tag & Tagarbejde - Agstner Byg ApS"
        description="Agstner Byg ApS tilbyder professionelt tagarbejde, lægning af nyt tag, tagrenovering og isætning af tagvinduer i Vissenbjerg og på Fyn. Få tilbud."
      />

      {/* Hero */}
      <section className="relative py-24 bg-zinc-950 overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00056_08b0e4cb.jpeg"
            alt="Tagarbejde Agstner Byg ApS"
            className="w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/85 via-zinc-950/50 to-zinc-950/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6">
            <div className="flex items-center gap-2 text-orange-500 font-display text-xs uppercase tracking-widest">
              <span className="w-6 h-0.5 bg-orange-500"></span>
              <span>YDELSE · AGSTNER BYG APS</span>
            </div>

            <h1 className="text-4xl sm:text-6xl font-display font-black text-white uppercase tracking-tight">
              Tagarbejde & Nyt Tag
            </h1>

            <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
              Nyt tag, tagrenovering og undertagsløsninger tilpasset danske vejrforhold og arkitektur.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <a
                href="/kontakt"
                className="px-8 py-4 rounded-none bg-orange-500 hover:bg-orange-600 text-zinc-950 font-display font-bold text-xs uppercase tracking-widest transition-all border border-orange-500"
              >
                Få et tilbud
              </a>

              <a
                href="tel:+4552502543"
                className="px-6 py-4 rounded-none bg-zinc-900 hover:bg-zinc-800 text-orange-500 font-display font-bold text-xs uppercase tracking-widest border border-zinc-800 transition-all whitespace-nowrap"
              >
                <span className="whitespace-nowrap">Ring på 52 50 25 43</span>
              </a>

              <a
                href="#projekter-sektion"
                className="px-4 py-4 text-zinc-300 hover:text-white font-display font-bold text-xs uppercase tracking-widest transition-colors flex items-center gap-2"
              >
                <span>Se projekter</span>
                <i className="fa-solid fa-chevron-down text-xs text-orange-500" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-20 bg-zinc-900 text-white border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start"
            style={{ '--left-col-height': contentHeight ? `${contentHeight}px` : 'auto' }}
          >
            
            <div ref={contentRef} className="lg:col-span-7 space-y-6">
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-4xl font-display font-bold text-white uppercase tracking-tight">
                  Beskyt din bolig med et professionelt udført tag
                </h2>
                <div className="text-zinc-300 text-sm leading-relaxed space-y-3">
                  <p>
                    Et tæt og veludført tag er afgørende for din boligs sundhed og levetid. Agstner Byg ApS står for komplet tagarbejde, herunder udskiftning af tagsten, reparation af eksisterende tage, opbygning af undertag, montage af kviste samt isætning af tagvinduer.
                  </p>
                  <p>
                    Vi rådgiver dig om valg af tagmaterialer og sikrer, at hele tagkonstruktionen udføres med høj præcision og korrekt ventilation.
                  </p>
                </div>
              </div>

              {/* Work Areas Grid */}
              <div>
                <h3 className="text-sm font-display font-bold uppercase tracking-widest text-orange-500 mb-4">
                  Tagopgaver vi varetager:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-zinc-300 font-mono uppercase">
                  {[
                    'Nyt tag',
                    'Tagrenovering',
                    'Reparation af tag',
                    'Udskiftning af tagsten',
                    'Undertag',
                    'Tagkonstruktion',
                    'Kviste',
                    'Tagvinduer'
                  ].map((task, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3.5 bg-zinc-950 border border-zinc-800">
                      <i className="fa-solid fa-check text-orange-500" aria-hidden="true"></i>
                      <span className="font-bold text-white">{task}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 w-full h-[360px] sm:h-[420px] lg:h-[var(--left-col-height)]">
              <div className="border border-zinc-800 bg-zinc-950 overflow-hidden w-full h-full">
                <video
                  src="https://assets.auxodev.dk/assets/bac61bd8-01e8-4025-a9be-38664430d08c/img-2391-1.mov"
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  controls
                  playsInline
                  preload="auto"
                >
                  Din browser understøtter ikke videoafspilning.
                </video>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="py-20 bg-zinc-950 border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8 border-b border-zinc-800 pb-4">
              <span className="text-xs font-display font-bold uppercase tracking-widest text-orange-500">BILLEDGALLERI</span>
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-white uppercase mt-1">
                Galleri over tagarbejde
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryImages.map((imgUrl, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative h-48 border border-zinc-800 focus:outline-none focus:border-orange-500 overflow-hidden"
                >
                  <img
                    src={imgUrl}
                    alt={`Tagarbejde ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-zinc-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <i className="fa-solid fa-magnifying-glass-plus text-xl text-orange-500" aria-hidden="true"></i>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Projects Section */}
      <section id="projekter-sektion" className="py-20 bg-zinc-900 text-white border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-10 pb-4 border-b border-zinc-800">
            <div>
              <span className="text-xs font-display font-bold uppercase tracking-widest text-orange-500">PROJEKTER</span>
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-white uppercase mt-1">
                Tagprojekter
              </h2>
            </div>
            <Link to="/projekter" className="text-xs font-display font-bold uppercase tracking-widest text-orange-500 hover:underline">
              Se alle projekter →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((proj) => (
              <div key={proj.id} className="bg-zinc-950 border border-zinc-800 p-5 group hover:border-orange-500 transition-all">
                <img
                  src={Array.isArray(proj.hovedbillede) ? proj.hovedbillede[0] : ''}
                  alt={proj.projektnavn}
                  className="w-full h-48 object-cover border border-zinc-800 mb-4"
                />
                <h3 className="font-display font-bold text-white text-lg uppercase group-hover:text-orange-500 transition-colors">{proj.projektnavn}</h3>
                <p className="text-xs font-mono text-orange-500 uppercase mt-1">{proj.omraade}</p>
                <p className="text-xs text-zinc-400 mt-2 line-clamp-2 leading-relaxed">{proj.kort_beskrivelse}</p>
                <div className="mt-4 pt-3 border-t border-zinc-800 flex justify-between items-center">
                  <Link to={`/projekter/${proj.url_slug}`} className="text-xs font-display font-bold uppercase tracking-widest text-orange-500 hover:underline">
                    Læs projekt →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {lightboxIndex !== null && (
        <GalleryLightbox
          images={galleryImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  )
}
